import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import JustificationContainer from '~/components/atom/JustificationContainer';
import LineTabs, { type LineTab } from '~/components/molecule/LineTabs';
import EventTimelineV2 from '~/components/template/EventTimelineV2';
import EventTimelineContent from '~/components/template/EventTimelineV2/components/EventTimelineContent';
import generateActivityV2 from '~/components/template/EventTimelineV2/utils/generateActivityV2';
import groupActivities from '~/components/template/EventTimelineV2/utils/groupActivities';
import { EventTimelineContact } from '~/components/template/EventTimelineV2';
import type { QueriedActivity } from '~/components/template/EventTimelineV2/types';
import { TimelineProvider } from '~/components/template/EventTimelineV2/context';
import type { TimelineActions } from '~/components/template/EventTimelineV2/context';
import FullEmailMessage from '~/LFA/components/pages/Contact/components/EventTimeline/components/FullEmailMessage';

const tabs: Array<LineTab> = [
  {
    label: 'Gerelateerde activiteit',
    key: 'related-activity',
    dataTestId: 'related-activity',
  },
  {
    label: 'Alle activiteiten',
    key: 'all-activities',
    dataTestId: 'all-activities',
  },
];

type Props = {
  contact: EventTimelineContact;
  activities: Array<QueriedActivity>;
};

const lfaTimelineActions: TimelineActions = {
  renderFullEmail: eventId => <FullEmailMessage eventId={eventId} />,
};

const TimelineSection: React.FCC<Props> = ({ activities, contact }) => {
  const [selectedTab, setSelectedTab] = useState<string>('related-activity');

  return (
    <JustificationContainer
      width="100%"
      direction="column"
      style={{
        flex: 1,
        display: 'flex',
        minHeight: 0,
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      <LineTabs
        justification="start"
        onSelect={key => setSelectedTab(key)}
        selectedKey={selectedTab}
        tabs={tabs}
      />
      <div
        style={{
          width: '100%',
          padding: '0 12px',
          flex: 1,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {selectedTab === 'related-activity' ? (
          <TimelineContainer>
            <TimelineProvider value={lfaTimelineActions}>
              <EventTimelineContent
                contact={contact}
                fetchUntilActivityFound={() => Promise.resolve(true)}
                groupedActivities={groupActivities(
                  activities.map(activity => generateActivityV2(activity)),
                )}
                targetActivityId={null}
              />
            </TimelineProvider>
          </TimelineContainer>
        ) : (
          <EventTimelineV2 contact={contact} />
        )}
      </div>
    </JustificationContainer>
  );
};

const TimelineContainer = styled.div<{ $disabled?: boolean }>(
  ({ theme, $disabled }) => css`
    width: 100%;
    position: relative;

    &:before {
      content: '';
      position: absolute;

      /* Account for height of the FIRST date label */
      top: 100px;
      left: ${theme.space('l')};
      /** Account for the height of the LAST icon */
      bottom: 100px;
      border-left: 2px dashed ${theme.color('tertiary', 'light')};
    }

    ${$disabled &&
    css`
      pointer-events: none;
      opacity: 50%;
      cursor: not-allowed;
    `};
  `,
);

export default TimelineSection;
